import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CommonImage from '@/components/CommonImage';
import { HEADER_STYLE } from '@/features/task-configuration/constants';

const DashboardCard = ({
	isShowCount = false,
	card,
	onEdit,
	onDelete,
	isReadOnly = false,
	shouldDisableFilter = false,
	onClickFilterItem = () => {},
	taskListCount = {},
	isLoadingDashboardTaskCount = false,
	hideDisabledFilters = false
}) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const navigate = useNavigate();
	const scrollRef = useRef(null);
	let headerStyle = HEADER_STYLE[0]; // Default style
	if (card?.titleStyle) {
		try {
			const parsedStyle = JSON.parse(card?.titleStyle);

			if (parsedStyle && parsedStyle?.bgColor && parsedStyle?.textColor && parsedStyle?.icon) {
				headerStyle = parsedStyle;
			} else {
				headerStyle = HEADER_STYLE?.find((style) => style?.id === parsedStyle?.id) || HEADER_STYLE[0];
			}
		} catch {
			headerStyle = HEADER_STYLE?.find((style) => style?.id === card?.titleStyle) || HEADER_STYLE[0];
		}
	}

	const headerIcon = HEADER_STYLE.some((style) => style.icon === headerStyle?.icon)
		? headerStyle.icon
		: HEADER_STYLE[0].icon;

	// Get filter list for display
	const filters = card?.filters || [];
	const sliceLimit = shouldDisableFilter ? 5 : 5;

	const countByFilterId = useMemo(() => {
		if (!taskListCount || typeof taskListCount !== 'object') return {};
		if (!Array.isArray(taskListCount)) return taskListCount;

		const countMap = {};
		taskListCount.forEach((item) => {
			const id = String(item?.id ?? '');
			if (id) countMap[id] = typeof item?.count === 'number' ? item.count : 0;
		});

		return countMap;
	}, [taskListCount]);

	const getCount = (filter) => {
		const matchedCount = countByFilterId?.[String(filter?.id ?? '')];
		return typeof matchedCount === 'number' ? matchedCount : (filter?.styleCount ?? 0);
	};

	const isFilterDisabled = (filter) =>
		shouldDisableFilter && !isLoadingDashboardTaskCount && !(getCount(filter) > 0);

	// Exclude disabled filters from the list entirely so enabled ones beyond the slice limit still show up
	const effectiveFilters = hideDisabledFilters
		? filters.filter((filter) => !isFilterDisabled(filter))
		: filters;
	const visibleFilters = isExpanded ? effectiveFilters : effectiveFilters.slice(0, sliceLimit);
	const remainingCount = effectiveFilters.length - sliceLimit;

	const handleFilterClick = (filter) => {
		if (isFilterDisabled(filter)) return;

		if (filter?.url) {
			if (isReadOnly) {
				window.location.assign(filter.url);
			} else {
				window.open(filter.url, '_blank');
			}

			return;
		}

		// Call the original callback
		onClickFilterItem?.(filter);
		// Navigate with query params for shareable URL
		const filterTitle = filter?.title ? encodeURIComponent(filter.title) : '';
		const defaultUrl = `/my-dashboard?tab=MY_DASHBOARD_STYLES&filterId=${filter.id}${
			filterTitle ? `&filterTitle=${encodeURIComponent(filterTitle)}` : ''
		}`;
		const targetUrl = filter?.url || defaultUrl;
		const isExternalUrl = /^https?:\/\//i.test(targetUrl);

		if (isReadOnly) {
			if (isExternalUrl) {
				window.location.assign(targetUrl);
			} else {
				navigate(targetUrl);
			}
		} else {
			window.open(targetUrl, '_blank');
		}
	};

	const handleViewMore = () => {
		setIsExpanded(!isExpanded);
	};

	useEffect(() => {
		if (isExpanded && scrollRef.current) {
			scrollRef.current.scrollTo({
				top: scrollRef.current.scrollHeight,
				behavior: 'smooth'
			});
		}
	}, [isExpanded]);

	const shouldHideEntireCard = hideDisabledFilters && filters.length > 0 && effectiveFilters.length === 0;

	if (shouldHideEntireCard) {
		return null;
	}
	const visibleRemainingCount = remainingCount;

	return (
		<div className="task-category-card shadow-2dp">
			<div
				className="task-category-header d-flex align-items-center justify-content-between mb-3 p-1"
				style={{ backgroundColor: headerStyle.bgColor }}
			>
				<div className="d-flex align-items-center">
					<CommonImage src={headerIcon} className="mr-2" />
					<p className="regular-14 weight-600" style={{ color: headerStyle.textColor }}>
						{card?.title || 'Untitled Card'}
					</p>
				</div>
				{!isReadOnly && (
					<div className="option">
						<div className="dropdown">
							<button
								className="btn btn-default dropdown-toggle"
								type="button"
								id={`menu-${card?.id}`}
								data-toggle="dropdown"
							>
								<CommonImage
									src="/icons/more_vertical.svg"
									alt="more_icon"
									width={24}
									height={24}
								/>
							</button>
							<ul className="dropdown-menu dropdown-menu-right shadow-lg">
								<li className="px-4 pb-3 pt-3">
									<span
										className="font-weight-normal text-black cursor-pointer"
										onClick={() => onEdit?.(card)}
									>
										Edit
									</span>
								</li>
								<li className="px-4 pb-3">
									<span
										className="font-weight-normal text-black cursor-pointer"
										onClick={() => onDelete?.(card)}
									>
										Delete
									</span>
								</li>
								<li className="px-4 pb-3">
									<span className="font-weight-normal text-black cursor-pointer">
										SL: {card?.serial}
									</span>
								</li>
							</ul>
						</div>
					</div>
				)}
			</div>
			<div className="task-category-body scroll-y-label" ref={scrollRef}>
				{filters.length > 0 ? (
					<ul className="task-list">
						{visibleFilters.map((filter, index) => {
							const count = getCount(filter);
							const disabled = isFilterDisabled(filter);

							return (
								<li
									key={filter.id || index}
									className={`regular-14 gray_dark_01 mb-2 d-block ${disabled ? 'cursor-none gray_dark_03' : ''}`}
									onClick={() => handleFilterClick(filter)}
								>
									{filter.title}
									{isShowCount && <span className="gray_dark_01"> ({count})</span>}
								</li>
							);
						})}
					</ul>
				) : (
					<p className="regular-14 gray_dark_01 text-center">No filters added</p>
				)}
			</div>
			{(isExpanded || visibleRemainingCount > 0) && (
				<div
					className="view-more d-flex align-items-center cursor-pointer mt-2"
					onClick={handleViewMore}
				>
					<p className="gray_dark_01 regular-14 weight-400 pb-1 mr-1">
						{isExpanded ? 'View less' : `View ${visibleRemainingCount} more`}
					</p>
					<CommonImage
						className="arrow-icon"
						src="/icons/down-arrow-gray-dark-01.svg"
						width={20}
						height={20}
						style={{
							transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
							transition: 'transform 0.3s ease'
						}}
					/>
				</div>
			)}
		</div>
	);
};

export default DashboardCard;
